import React, { useMemo, useState } from 'react';
import styled from 'styled-components';
import {
  Archive,
  CalendarOff,
  Edit3,
  Eye,
  Pause,
  Play,
  Plus,
  RefreshCw,
  Trash2,
} from 'lucide-react';
import {
  adminApi,
  merchantExperienceApi,
  extractErrorMessage,
  formatCurrency,
  formatNumber,
} from '@kardume/services';
import { useNotification } from '@kardume/hooks';
import type {
  ExperienceStatus,
  MerchantExperienceDto,
  MerchantExperienceSessionDto,
  Tone,
} from '@kardume/types';
import {
  EXPERIENCE_STATUS_LABELS,
  EXPERIENCE_TYPE_LABELS,
  MERCHANT_EXPERIENCE_ERROR_MESSAGES,
} from '@kardume/types';
import {
  ActionButton,
  Badge,
  Button,
  Modal,
  PageHeader,
  Pagination,
  SearchInput,
  Select,
  StatCard,
  Table,
  TableContainer,
  Tabs,
  Thumbnail,
} from '@kardume/ui';
import { useAsync } from '../utils/useAsync';
import DataState from '../components/DataState';
import ExperienceFormModal from '../components/experiences/ExperienceFormModal';
import ExperienceDetailDrawer from '../components/experiences/ExperienceDetailDrawer';
import ExperienceReviewsPanel from '../components/experiences/ExperienceReviewsPanel';
import SessionParticipantsModal from '../components/experiences/SessionParticipantsModal';

/**
 * Experiências no painel do lojista (card #347).
 *
 * ## O número que a tela existe para mostrar
 *
 * `upcomingSessions == 0` numa experiência **aprovada** significa que ela não
 * está em lugar nenhum: a vitrine só mostra o que tem data futura. O lojista
 * não tem como descobrir isso sozinho - do ponto de vista dele a experiência
 * foi aprovada e deveria estar no ar. Por isso o zero sai em vermelho na lista,
 * vira tarja no detalhe e é contado no cartão "Sem data futura".
 *
 * ## As avaliações ficam aqui, e não em "Avaliações"
 *
 * Nota de experiência e nota da loja respondem a perguntas diferentes e têm
 * médias separadas no core. A aba própria é o que impede que virem um número
 * só - que é o erro que o card #348 pede para não cometer.
 *
 * ## Aprovar não é da loja
 *
 * A loja pausa, retoma e encerra. Aprovar e recusar são da moderação do
 * Kardume, e o core recusa qualquer outra transição vinda daqui - os botões
 * refletem exatamente isso.
 */

const TabBody = styled.div`
  margin-top: 1.1rem;
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 1rem;
  margin-bottom: 1.25rem;

  @media (max-width: 900px) {
    grid-template-columns: repeat(2, 1fr);
  }
`;

const Toolbar = styled.div`
  display: flex;
  gap: 0.75rem;
  margin-bottom: 1rem;
  flex-wrap: wrap;
  align-items: center;

  > *:first-child {
    flex: 1;
    min-width: 220px;
  }
`;

const Item = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  min-width: 0;

  .info {
    min-width: 0;
  }
  strong {
    display: block;
    color: ${(p) => p.theme.textPrimary};
  }
  .slug {
    font-family: monospace;
    font-size: 0.68rem;
    color: ${(p) => p.theme.textMuted};
  }
`;

const Rejection = styled.div`
  margin-top: 0.3rem;
  font-size: 0.76rem;
  line-height: 1.45;
  color: ${(p) => p.theme.error};
`;

const Sessions = styled.span<{ $none: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  font-size: 0.85rem;
  font-weight: ${(p) => (p.$none ? 700 : 500)};
  color: ${(p) => (p.$none ? p.theme.error : p.theme.textSecondary)};
  white-space: nowrap;
`;

const RowActions = styled.div`
  display: flex;
  gap: 0.4rem;
  justify-content: flex-end;
`;

const ConfirmText = styled.p`
  margin: 0;
  font-size: 0.9rem;
  line-height: 1.55;
  color: ${(p) => p.theme.textSecondary};

  strong {
    color: ${(p) => p.theme.textPrimary};
  }

  & + & {
    margin-top: 0.75rem;
  }
`;

const STATUS_FILTER = [
  { value: '', label: 'Todas as situações' },
  { value: 'ACTIVE', label: 'No ar' },
  { value: 'PENDING_REVIEW', label: 'Em análise' },
  { value: 'PAUSED', label: 'Pausadas' },
  { value: 'REJECTED', label: 'Recusadas' },
  { value: 'ARCHIVED', label: 'Encerradas' },
  { value: 'DRAFT', label: 'Rascunhos' },
];

const PAGE_SIZE = 20;

/**
 * Quantas experiências no ar são lidas para contar as que estão sem data
 * futura. Uma loja com mais de 100 experiências ativas ao mesmo tempo não
 * existe hoje; se passar disso, o cartão conta as 100 primeiras e a lista
 * continua correta.
 */
const ACTIVE_SCAN_SIZE = 100;

function statusTone(status: ExperienceStatus): Tone {
  if (status === 'ACTIVE') return 'success';
  if (status === 'PENDING_REVIEW' || status === 'PAUSED' || status === 'DRAFT') return 'warning';
  if (status === 'REJECTED') return 'danger';
  return 'secondary';
}

interface Props {
  permissions: string[];
  isOwner: boolean;
}

const ExperiencesPage: React.FC<Props> = ({ permissions, isOwner }) => {
  const { showNotification } = useNotification();
  const canManage = isOwner || permissions.includes('ROLE_MERCHANT_EXPERIENCE_MANAGE');

  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage] = useState(0);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<MerchantExperienceDto | null>(null);
  const [detailId, setDetailId] = useState<string | null>(null);
  const [archiving, setArchiving] = useState<MerchantExperienceDto | null>(null);
  const [removing, setRemoving] = useState<MerchantExperienceDto | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [tab, setTab] = useState<'catalog' | 'reviews'>('catalog');
  const [participantsSession, setParticipantsSession] = useState<MerchantExperienceSessionDto | null>(
    null
  );

  const { data, loading, error, reload } = useAsync(
    () =>
      merchantExperienceApi.list({
        page,
        size: PAGE_SIZE,
        status: (statusFilter || undefined) as ExperienceStatus | undefined,
      }),
    [page, statusFilter]
  );

  /**
   * Os cartões não podem sair da página carregada: com paginação no servidor,
   * "3 no ar" seria só o que coube na tela. As contagens vêm de consultas
   * próprias - e a das ativas lê a lista inteira porque "sem data futura" não é
   * um filtro do servidor, é uma leitura de `upcomingSessions`.
   */
  const counters = useAsync(
    () =>
      Promise.all([
        merchantExperienceApi.list({ status: 'ACTIVE', size: ACTIVE_SCAN_SIZE }),
        merchantExperienceApi.list({ status: 'PENDING_REVIEW', size: 1 }),
        merchantExperienceApi.list({ status: 'REJECTED', size: 1 }),
      ]).then(([active, pending, rejected]) => ({
        active: active.totalElements,
        withoutDates: active.content.filter((item) => item.upcomingSessions === 0).length,
        pending: pending.totalElements,
        rejected: rejected.totalElements,
      })),
    []
  );

  const categories = useAsync(() => adminApi.listCategories(), []);
  const passPlans = useAsync(() => merchantExperienceApi.listPassPlanOptions(), []);

  const detail = useAsync(
    () => (detailId ? merchantExperienceApi.detail(detailId) : Promise.resolve(null)),
    [detailId]
  );

  const rows = useMemo(() => {
    const term = search.trim().toLowerCase();
    const content = data?.content ?? [];
    // A busca é local de propósito: o core não expõe `query` nesta rota, e a
    // página do lojista tem vinte linhas. Filtrar aqui é honesto porque o
    // recorte é a página que está na tela.
    return term ? content.filter((item) => item.title.toLowerCase().includes(term)) : content;
  }, [data, search]);

  const refreshAll = async (): Promise<void> => {
    await Promise.all([reload(), counters.reload()]);
  };

  const openCreate = (): void => {
    setEditing(null);
    setFormOpen(true);
  };

  const openEdit = (experience: MerchantExperienceDto): void => {
    setEditing(experience);
    setFormOpen(true);
  };

  const changeStatus = async (
    experience: MerchantExperienceDto,
    status: ExperienceStatus,
    successMessage: string
  ): Promise<void> => {
    setBusyId(experience.id);
    try {
      await merchantExperienceApi.changeStatus(experience.id, status);
      showNotification(successMessage, 'success');
      setArchiving(null);
      await refreshAll();
      if (detailId === experience.id) await detail.reload();
    } catch (err) {
      showNotification(
        extractErrorMessage(
          err,
          'Não foi possível mudar a situação da experiência.',
          MERCHANT_EXPERIENCE_ERROR_MESSAGES
        ),
        'error'
      );
    } finally {
      setBusyId(null);
    }
  };

  const confirmRemove = async (): Promise<void> => {
    if (!removing) return;
    setBusyId(removing.id);
    try {
      await merchantExperienceApi.remove(removing.id);
      showNotification('Experiência removida.', 'success');
      setRemoving(null);
      if (detailId === removing.id) setDetailId(null);
      await refreshAll();
    } catch (err) {
      showNotification(
        extractErrorMessage(
          err,
          'Não foi possível remover a experiência.',
          MERCHANT_EXPERIENCE_ERROR_MESSAGES
        ),
        'error'
      );
    } finally {
      setBusyId(null);
    }
  };

  return (
    <>
      <PageHeader
        title="Experiências"
        subtitle="O que a sua loja oferece para as pessoas viverem: degustação, jantar, aula, day use. Com agenda, vagas e fila de espera."
        actions={
          canManage &&
          tab === 'catalog' && (
            <Button variant="primary" onClick={openCreate}>
              <Plus size={16} /> Nova experiência
            </Button>
          )
        }
      />

      <Tabs
        value={tab}
        onChange={setTab}
        ariaLabel="Seções de Experiências"
        idPrefix="experiences"
        tabs={[
          { value: 'catalog', label: 'Catálogo' },
          { value: 'reviews', label: 'Avaliações' },
        ]}
      />

      {tab === 'reviews' ? (
        <TabBody>
          <ExperienceReviewsPanel canManage={canManage} />
        </TabBody>
      ) : (
        <TabBody>

      <StatsGrid>
        <StatCard label="No ar" value={formatNumber(counters.data?.active)} />
        <StatCard label="Sem data futura" value={formatNumber(counters.data?.withoutDates)} />
        <StatCard label="Em análise" value={formatNumber(counters.data?.pending)} />
        <StatCard label="Recusadas" value={formatNumber(counters.data?.rejected)} />
      </StatsGrid>

      <Toolbar>
        <SearchInput
          placeholder="Buscar pelo título nesta página..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <Select
          options={STATUS_FILTER}
          value={statusFilter}
          onChange={(e) => {
            setStatusFilter(e.target.value);
            setPage(0);
          }}
        />
        <ActionButton title="Atualizar" onClick={refreshAll}>
          <RefreshCw size={16} />
        </ActionButton>
      </Toolbar>

      <DataState
        loading={loading}
        error={error}
        isEmpty={rows.length === 0}
        emptyTitle="Nenhuma experiência ainda"
        emptyDescription="Crie a primeira: ela vai para a revisão do Kardume e, depois de aprovada e com data marcada, aparece na vitrine."
        onRetry={reload}
      >
        <TableContainer>
          <Table>
            <thead>
              <tr>
                <th>Experiência</th>
                <th>Formato</th>
                <th>Situação</th>
                <th>Preço</th>
                <th>Datas futuras</th>
                <th style={{ textAlign: 'right' }}>Ações</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((experience) => (
                <tr key={experience.id}>
                  <td>
                    <Item>
                      <Thumbnail
                        src={experience.coverImageUrl ?? undefined}
                        alt={experience.title}
                        width={72}
                      />
                      <div className="info">
                        <strong>{experience.title}</strong>
                        <div className="slug">/{experience.slug}</div>
                        {experience.status === 'REJECTED' && (
                          <Rejection>
                            Recusada: {experience.rejectionReason || 'sem motivo registrado'}. Editar
                            reenvia para revisão.
                          </Rejection>
                        )}
                      </div>
                    </Item>
                  </td>
                  <td>
                    <Badge tone="secondary">{EXPERIENCE_TYPE_LABELS[experience.type]}</Badge>
                  </td>
                  <td>
                    <Badge tone={statusTone(experience.status)}>
                      {EXPERIENCE_STATUS_LABELS[experience.status]}
                    </Badge>
                  </td>
                  <td style={{ whiteSpace: 'nowrap' }}>
                    {experience.free ? 'Gratuita' : formatCurrency(experience.price)}
                  </td>
                  <td>
                    <Sessions
                      $none={experience.upcomingSessions === 0}
                      title={
                        experience.upcomingSessions === 0
                          ? 'Sem data futura: não aparece na vitrine'
                          : undefined
                      }
                    >
                      {experience.upcomingSessions === 0 && <CalendarOff size={14} />}
                      {formatNumber(experience.upcomingSessions)}
                    </Sessions>
                  </td>
                  <td>
                    <RowActions>
                      <ActionButton
                        aria-label="Abrir experiência"
                        title="Fotos e agenda"
                        onClick={() => setDetailId(experience.id)}
                      >
                        <Eye size={16} />
                      </ActionButton>
                      {canManage && (
                        <>
                          <ActionButton
                            aria-label="Editar experiência"
                            title="Editar"
                            onClick={() => openEdit(experience)}
                          >
                            <Edit3 size={16} />
                          </ActionButton>
                          {experience.status === 'ACTIVE' && (
                            <ActionButton
                              aria-label="Pausar experiência"
                              title="Pausar (some da vitrine, mantém as reservas)"
                              disabled={busyId === experience.id}
                              onClick={() =>
                                changeStatus(experience, 'PAUSED', 'Experiência pausada.')
                              }
                            >
                              <Pause size={16} />
                            </ActionButton>
                          )}
                          {experience.status === 'PAUSED' && (
                            <ActionButton
                              aria-label="Retomar experiência"
                              title="Retomar"
                              disabled={busyId === experience.id}
                              onClick={() => changeStatus(experience, 'ACTIVE', 'Experiência retomada.')}
                            >
                              <Play size={16} />
                            </ActionButton>
                          )}
                          {experience.status !== 'ARCHIVED' && (
                            <ActionButton
                              aria-label="Encerrar experiência"
                              title="Encerrar"
                              onClick={() => setArchiving(experience)}
                            >
                              <Archive size={16} />
                            </ActionButton>
                          )}
                          <ActionButton
                            aria-label="Remover experiência"
                            title="Remover"
                            color="#DC2626"
                            onClick={() => setRemoving(experience)}
                          >
                            <Trash2 size={16} />
                          </ActionButton>
                        </>
                      )}
                    </RowActions>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </TableContainer>

        {(data?.totalPages ?? 0) > 1 && (
          <Pagination
            currentPage={(data?.number ?? 0) + 1}
            totalPages={data?.totalPages ?? 1}
            totalItems={data?.totalElements}
            pageSize={PAGE_SIZE}
            onPageChange={(next) => setPage(next - 1)}
          />
        )}
      </DataState>
        </TabBody>
      )}

      <ExperienceFormModal
        isOpen={formOpen}
        onClose={() => setFormOpen(false)}
        experience={editing}
        categories={categories.data ?? []}
        passPlans={passPlans.data ?? []}
        onSaved={refreshAll}
      />

      <ExperienceDetailDrawer
        isOpen={detailId !== null}
        onClose={() => setDetailId(null)}
        detail={detail.data ?? null}
        loading={detail.loading}
        error={detail.error}
        canManage={canManage}
        onReload={async () => {
          await detail.reload();
          await refreshAll();
        }}
        onOpenParticipants={setParticipantsSession}
      />

      <SessionParticipantsModal
        session={participantsSession}
        experienceTitle={detail.data?.experience.title ?? 'Experiência'}
        canManage={canManage}
        onClose={() => setParticipantsSession(null)}
        onChanged={async () => {
          // Marcar falta muda os contadores da data: a agenda do detalhe
          // precisa ser relida para não mostrar número velho.
          await detail.reload();
        }}
      />

      <Modal
        isOpen={archiving !== null}
        onClose={() => setArchiving(null)}
        title="Encerrar experiência"
        width="480px"
        footer={
          <>
            <Button variant="ghost" onClick={() => setArchiving(null)}>
              Voltar
            </Button>
            <Button
              variant="danger"
              isLoading={busyId === archiving?.id}
              onClick={() => archiving && changeStatus(archiving, 'ARCHIVED', 'Experiência encerrada.')}
            >
              Encerrar
            </Button>
          </>
        }
      >
        <ConfirmText>
          Encerrar <strong>{archiving?.title}</strong>?
        </ConfirmText>
        <ConfirmText>
          Ela sai da vitrine e fica no histórico. As datas já marcadas continuam existindo - se você
          não vai mais realizá-las, cancele-as antes, para quem reservou ser avisado.
        </ConfirmText>
      </Modal>

      <Modal
        isOpen={removing !== null}
        onClose={() => setRemoving(null)}
        title="Remover experiência"
        width="480px"
        footer={
          <>
            <Button variant="ghost" onClick={() => setRemoving(null)}>
              Voltar
            </Button>
            <Button variant="danger" isLoading={busyId === removing?.id} onClick={confirmRemove}>
              Remover
            </Button>
          </>
        }
      >
        <ConfirmText>
          Remover <strong>{removing?.title}</strong>?
        </ConfirmText>
        <ConfirmText>
          Só é possível remover quando não há data futura com reserva. Se houver, cancele essas datas
          primeiro: é o cancelamento que avisa quem tinha lugar.
        </ConfirmText>
      </Modal>
    </>
  );
};

export default ExperiencesPage;
